import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ExternalLink } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { SetupSteps } from '../Steps';

const azureFormSchema = z.object({
  tenantId: z.string().trim().min(1, 'Enter your tenant ID to continue'),
});

export type IAzureForm = z.infer<typeof azureFormSchema>;

type Props = {
  onSubmit: (data: IAzureForm) => void;
  buttonText?: string;
};

const instructions = [
  <Fragment key="1">
    Log in to the <strong>Azure portal</strong>.
  </Fragment>,
  <Fragment key="2">
    Select <strong>Microsoft Entra ID</strong> from the Azure services list, then open <strong>Overview</strong>.
  </Fragment>,
  <span className="font-bold" key="3">
    Copy your tenant ID and paste it into the field below.
  </span>,
];

const AzureForm = ({ onSubmit, buttonText = 'Connect to Azure' }: Props) => {
  const form = useForm<IAzureForm>({
    resolver: zodResolver(azureFormSchema),
    defaultValues: {
      tenantId: '',
    },
  });

  return (
    <Form {...form}>
      <SetupSteps items={instructions} />
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="tenantId"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Tenant ID</FormLabel>
              <FormControl>
                <Input {...field} placeholder="e.g., xxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          isLoading={form.formState.isSubmitting}
          disabled={form.formState.isSubmitting}
          size="sm"
          className="w-full"
        >
          {buttonText} <ExternalLink />
        </Button>
      </form>
    </Form>
  );
};

export default AzureForm;
